import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Menu, X, User, LogOut, Home, BookOpen, HelpCircle, Users, Shield, Palette, Upload } from 'lucide-react';
import Logo from './Logo';
import NotificationBell from './NotificationBell';
import CelebrationModal from './CelebrationModal';
import { WhatsAppCommunityModal, WhatsAppGlyph } from './community/WhatsAppCommunity';
import { useNotifications } from '../hooks/useNotifications';
import { useUserStats } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname === '/admin';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const { scrollY } = useScroll();
  const { user, isLoggedIn, openAuthModal, logout } = useAuth();
  const { setShowThemeSelector } = useTheme();
  const { unreadCount, celebration, refresh, dismissCelebration } = useNotifications(isLoggedIn);
  const { stats: userStats } = useUserStats({ enabled: isLoggedIn });

  const headerBg = useTransform(
    scrollY,
    [0, 100],
    ['rgba(6, 9, 24, 0.3)', 'rgba(6, 9, 24, 0.92)']
  );
  const headerBlur = useTransform(scrollY, [0, 100], [10, 30]);
  const headerPadding = useTransform(scrollY, [0, 100], [18, 10]);
  const headerBorder = useTransform(
    scrollY,
    [0, 100],
    ['rgba(51, 65, 85, 0)', 'rgba(51, 65, 85, 0.2)']
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveSection('');
      return;
    }
    const handleScroll = () => {
      const sections = ['notes', 'daily-questions', 'community'];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom >= 200) {
            setActiveSection(id);
            return;
          }
        }
      }
      if (window.scrollY < 300) setActiveSection('');
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const handleLogin = () => {
    openAuthModal('login');
  };

  const navItems = [
    {
      id: '',
      label: 'Ana Sayfa',
      icon: Home,
      action: () => {
        navigate('/');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    },
    {
      id: 'notes',
      label: 'Notlar',
      icon: BookOpen,
      action: () => {
        navigate('/notlar');
      },
    },
    {
      id: 'daily-questions',
      label: 'Günlük Sorular',
      icon: HelpCircle,
      action: () => {
        if (location.pathname === '/') scrollToSection('daily-questions');
        else navigate('/#daily-questions');
      },
    },
    {
      id: 'community',
      label: 'Topluluk',
      icon: Users,
      action: () => {
        navigate('/forum');
        setActiveSection('community');
      },
    },
  ];

  return (
    <motion.header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: headerBg,
        backdropFilter: `blur(${headerBlur}px)`,
        WebkitBackdropFilter: `blur(${headerBlur}px)`,
        borderBottom: `1px solid`,
        borderColor: headerBorder,
        paddingTop: headerPadding,
        paddingBottom: headerPadding,
      }}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      <div style={{
        maxWidth: '1300px',
        margin: '0 auto',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <motion.div whileHover={{ scale: 1.03 }} transition={{ type: 'spring', stiffness: 400 }}>
          <Logo />
        </motion.div>

        {/* Desktop Navigation */}
        {!isMobile && (
          <nav style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {navItems.map((item) => {
              const isActive = activeSection === item.id;
              const Icon = item.icon;
              return (
                <motion.button
                  type="button"
                  key={item.id}
                  onClick={item.action}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 18px',
                    borderRadius: '12px',
                    border: 'none',
                    background: isActive ? 'var(--accent-primary-soft)' : 'transparent',
                    color: isActive ? 'var(--accent-primary)' : '#94a3b8',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Icon size={15} />
                  <span>{item.label}</span>
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      style={{
                        position: 'absolute',
                        bottom: '0',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '20px',
                        height: '2px',
                        borderRadius: '1px',
                        background: 'var(--accent-primary)',
                      }}
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}
                </motion.button>
              );
            })}
          </nav>
        )}

        {/* Desktop Auth Area */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <motion.button
              type="button"
              whileHover={{ scale: 1.05, boxShadow: '0 6px 20px rgba(37, 211, 102, 0.35)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setWaOpen(true)}
              aria-label="WhatsApp topluluklarımız"
              className="wa-nav-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                background: 'linear-gradient(135deg, #25D366, #128C7E)',
                border: 'none',
                color: '#fff',
                padding: '8px 14px',
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(37, 211, 102, 0.3)',
              }}
            >
              <WhatsAppGlyph size={16} />
              <span>Topluluk</span>
            </motion.button>
            <motion.button
              type="button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowThemeSelector(true)}
              aria-label="Tema seç"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(51, 65, 85, 0.35)',
                color: '#94a3b8',
                padding: '8px 12px',
                borderRadius: '10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Palette size={16} />
            </motion.button>
          {isLoggedIn && user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/notlar/yukle')}
                className="btn-shimmer btn-primary"
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Upload size={14} />
                <span>Not Yükle</span>
              </motion.button>
              {user.role === 'admin' && (
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (isAdminRoute ? navigate('/') : navigate('/admin'))}
                  style={{
                    background: isAdminRoute ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    color: '#a78bfa',
                    padding: '8px 14px',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Shield size={14} />
                  <span>{isAdminRoute ? 'Ana Sayfa' : 'Admin'}</span>
                </motion.button>
              )}
              <NotificationBell unreadCount={unreadCount} onRefresh={refresh} />
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/hesabim')}
                className="header-account-link"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 6px',
                  borderRadius: '10px',
                }}
              >
                <img
                  src={user.avatar}
                  alt={user.name}
                  style={{ width: '32px', height: '32px', borderRadius: '10px', border: '2px solid var(--accent-primary-border)' }}
                />
                <span style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '500' }}>{user.name}</span>
                {userStats && (
                  <span className="header-points-chip">{userStats.points} puan</span>
                )}
              </motion.button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={logout}
                style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  color: '#ef4444',
                  padding: '8px 16px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <LogOut size={14} />
                <span>Çıkış</span>
              </motion.button>
            </div>
          ) : (
            <motion.button
              type="button"
              whileHover={{ scale: 1.05, boxShadow: 'var(--shadow-glow-lg)' }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLogin}
              className="btn-shimmer btn-primary"
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <User size={15} />
              <span>Giriş Yap</span>
            </motion.button>
          )}
          </div>
        )}

        {/* Mobile Menu Button */}
        {isMobile && (
          <motion.button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileTap={{ scale: 0.9 }}
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(51, 65, 85, 0.3)',
              color: 'white',
              cursor: 'pointer',
              padding: '10px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AnimatePresence mode="wait">
              {isMobileMenuOpen ? (
                <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
                  <X size={22} />
                </motion.div>
              ) : (
                <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
                  <Menu size={22} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        )}
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{
              overflow: 'hidden',
              background: 'rgba(6, 9, 24, 0.95)',
              backdropFilter: 'blur(30px)',
              borderTop: '1px solid rgba(51, 65, 85, 0.2)',
            }}
          >
            <nav style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {navItems.map((item, i) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <motion.button
                    type="button"
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => { item.action(); setIsMobileMenuOpen(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: 'none',
                      background: isActive ? 'var(--accent-primary-soft)' : 'transparent',
                      color: isActive ? 'var(--accent-primary)' : '#cbd5e1',
                      fontSize: '15px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left' as const,
                    }}
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                  </motion.button>
                );
              })}
              <motion.button
                type="button"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navItems.length * 0.05 }}
                onClick={() => { setWaOpen(true); setIsMobileMenuOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
                  borderRadius: '12px', border: 'none',
                  background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.18), rgba(18, 140, 126, 0.12))',
                  color: '#4ade80', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
                  width: '100%', textAlign: 'left' as const,
                }}
              >
                <WhatsAppGlyph size={18} color="#4ade80" />
                <span>WhatsApp Topluluk</span>
              </motion.button>
              <motion.button
                type="button"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navItems.length * 0.05 }}
                onClick={() => { setShowThemeSelector(true); setIsMobileMenuOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
                  borderRadius: '12px', border: 'none', background: 'rgba(30, 41, 59, 0.35)',
                  color: '#cbd5e1', fontSize: '15px', fontWeight: '500', cursor: 'pointer',
                  width: '100%', textAlign: 'left' as const,
                }}
              >
                <Palette size={18} />
                <span>Tema</span>
              </motion.button>
              {isLoggedIn && user && (
              <motion.button
                type="button"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navItems.length * 0.05 }}
                onClick={() => { navigate('/notlar/yukle'); setIsMobileMenuOpen(false); }}
                className="btn-primary"
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
                  fontSize: '15px', fontWeight: '600', width: '100%', marginTop: '8px',
                }}
              >
                <Upload size={18} />
                <span>Not Yükle</span>
              </motion.button>
              )}
              {isLoggedIn && user ? (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (navItems.length + 1) * 0.05 }}
                  style={{ display: 'flex', flexDirection: 'column' as const, gap: '8px', marginTop: '8px' }}
                >
                  <button
                    type="button"
                    onClick={() => { navigate('/hesabim'); setIsMobileMenuOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
                      borderRadius: '12px', border: 'none',
                      background: 'var(--accent-primary-soft)', color: 'var(--accent-primary)',
                      fontSize: '15px', fontWeight: '600', cursor: 'pointer', width: '100%', textAlign: 'left' as const,
                    }}
                  >
                    <User size={18} />
                    <span>Hesabım</span>
                    {userStats && (
                      <span style={{ marginLeft: 'auto', fontSize: '12px', opacity: 0.9 }}>{userStats.points} puan</span>
                    )}
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px' }}>
                    <img src={user.avatar} alt={user.name} style={{ width: '28px', height: '28px', borderRadius: '8px' }} />
                    <span style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '500' }}>{user.name}</span>
                  </div>
                  {user.role === 'admin' && (
                    <button
                      onClick={() => {
                        isAdminRoute ? navigate('/') : navigate('/admin');
                        setIsMobileMenuOpen(false);
                      }}
                      style={{
                        background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)',
                        color: '#a78bfa', padding: '12px', borderRadius: '12px', fontSize: '14px',
                        fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: '8px',
                      }}
                    >
                      <Shield size={16} />
                      <span>{isAdminRoute ? 'Ana Sayfa' : 'Admin Paneli'}</span>
                    </button>
                  )}
                  <button
                    onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                    style={{
                      background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)',
                      color: '#ef4444', padding: '12px', borderRadius: '12px', fontSize: '14px',
                      fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', gap: '8px',
                    }}
                  >
                    <LogOut size={16} />
                    <span>Çıkış Yap</span>
                  </button>
                </motion.div>
              ) : (
                <motion.button
                  type="button"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navItems.length * 0.05 }}
                  onClick={() => { handleLogin(); setIsMobileMenuOpen(false); }}
                  style={{
                    background: 'var(--gradient-primary)',
                    color: 'white', padding: '14px', borderRadius: '12px', border: 'none',
                    fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: '8px', marginTop: '8px', fontSize: '15px',
                    boxShadow: '0 4px 15px var(--shadow-glow)',
                  }}
                >
                  <User size={16} />
                  <span>Giriş Yap</span>
                </motion.button>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
      <CelebrationModal notification={celebration} onClose={dismissCelebration} />
      <WhatsAppCommunityModal open={waOpen} onClose={() => setWaOpen(false)} />
    </motion.header>
  );
};

export default Header;
